import { DatamapDoc, DatamapParseResult, DatamapRow, DatamapValue } from "types/datamap.types"

const UTF8_BOM = "\uFEFF"
const REQUIRED = ["LABEL", "TYPE", "VARIABLE", "VALUES"] as const

/**
 * A datamap's field separator, chosen as the engine chooses it (SeqUtils GuessCSVFieldSeparator):
 * commas, tabs and semicolons outside quotes are counted up to the first line end, so the whole
 * file may be passed. Tab or semicolon wins only with more than both others; otherwise comma.
 */
export const detectSeparator = (headerLine: string): "," | "\t" | ";" => {
  let commas = 0
  let tabs = 0
  let semicolons = 0
  let inQuotes = false
  for (const char of headerLine) {
    if (char === "\r" || char === "\n") break
    if (char === '"') inQuotes = !inQuotes
    else if (!inQuotes && char === ",") commas++
    else if (!inQuotes && char === "\t") tabs++
    else if (!inQuotes && char === ";") semicolons++
  }
  if (tabs > commas && tabs > semicolons) return "\t"
  if (semicolons > commas && semicolons > tabs) return ";"
  return ","
}

/** Drops the line ends at the very end of the file, as the engine does (SeqUtils EndOfLine). */
const stripTrailingLineEnds = (text: string): string => {
  let end = text.length
  while (end > 0 && (text[end - 1] === "\r" || text[end - 1] === "\n")) end--
  return text.slice(0, end)
}

/**
 * Parses CSV text into records of cells in one pass. A quote opens a quoted cell only at the
 * start of a cell (RFC 4180), "" inside quotes is a quote, and newlines inside quotes stay in the
 * cell. Cells are never trimmed: the import matches labels byte for byte.
 */
export const parseCsv = (text: string, separator: string): string[][] => {
  const records: string[][] = []
  let cells: string[] = []
  let current = ""
  let inQuotes = false
  let atCellStart = true

  const endCell = (): void => {
    cells.push(current)
    current = ""
    atCellStart = true
  }
  const endRecord = (): void => {
    endCell()
    records.push(cells)
    cells = []
  }

  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        current += char
      }
      continue
    }
    if (char === '"' && atCellStart) {
      inQuotes = true
      atCellStart = false
      continue
    }
    if (char === separator) {
      endCell()
      continue
    }
    if (char === "\r" || char === "\n") {
      if (char === "\r" && text[i + 1] === "\n") i++
      endRecord()
      continue
    }
    current += char
    atCellStart = false
  }
  if (current !== "" || cells.length > 0) endRecord()
  return records
}

/**
 * Reads a datamap CSV. Header names are matched without regard to case or surrounding spaces;
 * `headerExact` says whether they were already exactly what the engine needs (the editor writes
 * them exactly on save, an upload sends the file as it is). Empty code,label pairs are padding
 * (Excel pads rows to the widest one) and are dropped. All-empty records are dropped too, before
 * the header is looked for, and `blankRows` counts them: the engine refuses a file with an empty
 * row. Like the engine, line ends at the very end of the file make no rows.
 */
export const parseDatamapCsv = (input: string): DatamapParseResult => {
  const text = stripTrailingLineEnds(input.startsWith(UTF8_BOM) ? input.slice(1) : input)
  const parsed = parseCsv(text, detectSeparator(text))
  const records = parsed.filter((cells) => cells.some((cell) => cell !== ""))
  const blankRows = parsed.length - records.length
  if (records.length === 0) return { ok: false, error: "The datamap is empty." }

  const header = records[0]
  const names = header.map((cell) => cell.trim().toUpperCase())
  for (const name of REQUIRED) {
    if (!names.includes(name))
      return {
        ok: false,
        error: `The header row has no ${name} column. It needs LABEL, TYPE, VARIABLE and VALUES, with VALUES last.`,
      }
    if (names.indexOf(name) !== names.lastIndexOf(name))
      return { ok: false, error: `The header row has more than one ${name} column.` }
  }
  if (names.indexOf("FIELD_METADATA") !== names.lastIndexOf("FIELD_METADATA"))
    return { ok: false, error: "The header row has more than one FIELD_METADATA column." }

  const labelIdx = names.indexOf("LABEL")
  const typeIdx = names.indexOf("TYPE")
  const variableIdx = names.indexOf("VARIABLE")
  const fieldMetadataIdx = names.indexOf("FIELD_METADATA")
  const valuesIdx = names.indexOf("VALUES")
  if ([labelIdx, typeIdx, variableIdx, fieldMetadataIdx].some((i) => i > valuesIdx))
    return { ok: false, error: "VALUES must be the last column of the header row." }

  const known = [labelIdx, typeIdx, variableIdx, valuesIdx, fieldMetadataIdx].filter((i) => i >= 0)
  const headerExact = known.every((i) => header[i] === names[i])

  const cell = (cells: string[], i: number): string => (i >= 0 && i < cells.length ? cells[i] : "")

  const rows: DatamapRow[] = records.slice(1).map((cells, n) => {
    const values: DatamapValue[] = []
    for (let i = valuesIdx; i < cells.length; i += 2) {
      const code = cell(cells, i)
      const label = cell(cells, i + 1)
      if (code === "" && label === "") continue
      values.push({ code, label })
    }
    return {
      id: `r${n + 1}`,
      label: cell(cells, labelIdx),
      type: cell(cells, typeIdx),
      variable: cell(cells, variableIdx),
      fieldMetadata: cell(cells, fieldMetadataIdx),
      values,
    }
  })

  return { ok: true, doc: { rows, hasFieldMetadata: fieldMetadataIdx >= 0 }, headerExact, blankRows }
}

const escapeCell = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value

/** Writes a datamap the engine reads: comma separated, exact header names, one code,label pair per two cells. */
export const serializeDatamapCsv = (doc: DatamapDoc): string => {
  const withText = doc.hasFieldMetadata || doc.rows.some((row) => row.fieldMetadata !== "")
  const header = withText ? "LABEL,TYPE,VARIABLE,FIELD_METADATA,VALUES" : "LABEL,TYPE,VARIABLE,VALUES"
  const lines = doc.rows.map((row) => {
    const cells = [row.label, row.type, row.variable]
    if (withText) cells.push(row.fieldMetadata)
    for (const value of row.values) cells.push(value.code, value.label)
    return cells.map(escapeCell).join(",")
  })
  return [header, ...lines].join("\n") + "\n"
}
